import { all, call, delay, put, select } from 'redux-saga/effects';
import { format } from 'date-fns';
import {
  getCSVToExport,
  getCSVToExportSuccess,
  getCSVToExportFailure,
  closeModal,
  setModalData,
  postCommentToStoplightSuccess,
  postNewTaskSuccess,
  postNewTaskFailure,
  completeTaskSuccess,
  completeTaskFailure,
  deleteTaskSuccess,
  deleteTaskFailure,
  getCategoriesByTypeSuccess,
  getCategoriesByTypeFailure,
  getDetailsByTicketNumSuccess,
  getDetailsByTicketNumFailure,
  getSCLocationsSuccess,
  getSCLocationsFailure,
  getGELocationsSuccess,
  getGELocationsFailure,
  verifyControlNumberSuccess,
  verifyControlNumberFailure,
  searchIssuesSuccess,
  searchIssuesFailure,
  getIssuesByUnitIdsOrEmployeeListSuccess,
  getIssuesByUnitIdsOrEmployeeListFailure,
  getIssueReportByFacilityIdSuccess,
  getIssueReportByFacilityIdFailure,
  getFeedbackByUnitIdsSuccess,
  getFeedbackByUnitIdsRecognitionMetricsSuccess,
  getFeedbackByUnitIdsFailure,
  getFeedbackByEmployeeListSuccess,
  getFeedbackByEmployeeListFailure,
  getFeedbackReportByFacilityIdSuccess,
  getFeedbackReportByFacilityIdFailure,
  clearSharingQ,
  shareAllTasks,
  shareAllTasksSuccess,
  shareAllTasksFailure,
  getTasksByAccountNum,
  getTasksByEmployeeId,
  getTasksByAccountNumSuccess,
  getTasksByAccountNumFailure,
  getTasksByEmployeeIdSuccess,
  getTasksByEmployeeIdFailure,
  getIssuesByUnitIdsOrEmployeeMetricsSuccess,
  getFeedbackByUnitIdsCoachingMetricsSuccess,
  setToastOptions,
  slideIn
} from '../actions';
import { TasksApi } from '../apis';

import { getData, postData } from '../../services/API';
import {
  completeTaskUrl,
  deleteTaskUrl,
  discussionUrl,
  feedbackByFacilityUrl,
  getCategoriesByTypeUrl,
  getEmployeeIssuesUrl,
  getFeedbackByEmployeeListUrl,
  getFeedbackByUnitsIdsUrl,
  getECLocationsNewUrl,
  getGELocationsUrl,
  getPatientIssuesUrl,
  getTasksByAccountNumberUrl,
  getTasksByEmployeeIdUrl,
  issueSearchUrl,
  issuesByFacilityUrl,
  postNewTaskUrl,
  shareTaskUrl,
  tasksByIdUrl,
  ticketDetailsUrl,
  verifyControlNumberUrl
} from '../../services/API/endpoints';

import { useIssuesApi, useFeedbackApi } from '../../utils/api';
import { date, text } from '../../utils/helpers';

// Issue
export function* postNewTaskSaga({ payload }) {
  const url = postNewTaskUrl;
  const { facilityId, taskType, roundId, accountNum, employeeId, taskSubtype } =
    payload;

  try {
    const { data, status } = yield call(postData, url, payload);
    const { authorizedUser } = yield select(state => state.AuthorizedUser);
    const { selectedUnits, selectedEmpDepartments } = yield select(
      state => state.ConfigReducer
    );
    const { patientSummaryReportFilter, employeeSummaryReportFilter } =
      yield select(state => state.ReportReducer);

    const { selectedDelegatedEmployee } = yield select(
      state => state.EmployeeReducer
    );
    const {
      currentFeedbackPage,
      currentIssuePage,
      feedbackFilter,
      issueFilter,
      sortKey,
      sortDir,
      taskSearchQuery
    } = yield select(state => state.TaskReducer);
    const { userSection } = yield select(state => state.UserReducer);

    if (status === 200) {
      const toastMsg =
        taskType === 'issue' ? 'Issue Posted' : 'Feedback Posted';
      yield put(
        setToastOptions({
          msg: toastMsg,
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      yield delay(2100);
      // TODO: Bug this only shares on newly added
      const sharingQ = yield select(state => state.TaskReducer.sharingQ);

      // Only call the share saga if there are users in the Q to share task with
      if (sharingQ.length > 0) {
        const taskShare = sharingQ.filter(
          item => item.taskId === data[0].taskId
        );
        yield put(shareAllTasks(taskShare[0]));
        yield put(
          setToastOptions({
            msg: `Task${
              sharingQ.length > 1 ? 's have been shared!' : ' has been shared!'
            }`,
            feedback: 'success',
            duration: 1000,
            speed: 500
          })
        );
        yield put(slideIn());
      }
      yield put(shareAllTasksSuccess(data));
      if (!payload.multiShare) {
        yield put(clearSharingQ());
      } else {
        // to signal successful post when there is a list in the sharing queue
        yield put({
          type: 'POST_NEW_TASK_SUCCESS',
          taskId: payload.taskId
        });
      }

      // update tasks with new data if not on round
      if (
        (userSection === 'patient' ||
          userSection === 'employee' ||
          userSection === 'csc' ||
          userSection === 'cned') &&
        (window.location.pathname.includes('/issues') ||
          window.location.pathname.includes('/feedback'))
      ) {
        if (taskType === 'issue') {
          const issuesAction = useIssuesApi({
            authorizedUser,
            currentIssuePage,
            issueFilter,
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortDir,
            sortKey
          });
          yield put(issuesAction);
        }
        if (taskType === 'feedback') {
          const feedbackAction = useFeedbackApi({
            authorizedUser,
            currentFeedbackPage,
            feedbackFilter,
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortKey,
            sortDir
          });
          yield put(feedbackAction);
        }
      } else if (window.location.pathname.includes('/profile')) {
        if (!roundId) {
          if (employeeId) {
            yield put(
              getTasksByEmployeeId({
                employeeId,
                roundingTypes: ['employee', 'cned', 'csrn']
              })
            );
          } else if (accountNum) {
            yield put(
              // patient tasks
              getTasksByAccountNum({
                facilityMnemonic: facilityId,
                accountNumber: accountNum
              })
            );
          }
        }
      } else if (window.location.pathname.includes('/summary')) {
        let taskFilters = {
          category: '',
          subCategory: '',
          createdBy: '',
          patient: ''
        };
        if (window.location.pathname.includes('patient/summary')) {
          taskFilters = {
            ...taskFilters,
            ...patientSummaryReportFilter,
            startDate:
              patientSummaryReportFilter.startDate ??
              format(new Date(), 'yyyy-MM-dd')
          };
        } else {
          taskFilters = {
            ...taskFilters,
            ...employeeSummaryReportFilter,
            startDate:
              employeeSummaryReportFilter.startDate ??
              format(new Date(), 'yyyy-MM-dd')
          };
        }
        if (taskType === 'issue') {
          const issuesAction = useIssuesApi({
            authorizedUser,
            currentIssuePage,
            issueFilter: {
              ...taskFilters
            },
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortDir,
            sortKey,
            summaryView: 'patient'
          });
          yield put(issuesAction);
        }
        if (taskType === 'feedback') {
          const feedbackAction = useFeedbackApi({
            authorizedUser,
            currentFeedbackPage,
            feedbackFilter: {
              ...taskFilters,
              employeeId: '',
              taskSubType: taskSubtype
            },
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortKey,
            sortDir,
            summaryView: 'patient'
          });
          yield put(feedbackAction);
        }
      }

      yield put(postNewTaskSuccess());
      return yield put(closeModal());
    }
    yield put(postNewTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Post!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(postNewTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Post!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}

export function* completeTaskSaga({ payload }) {
  const { deepLink, edit, taskId, taskType } = payload;

  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { selectedUnits, selectedEmpDepartments } = yield select(
    state => state.ConfigReducer
  );
  const { selectedDelegatedEmployee } = yield select(
    state => state.EmployeeReducer
  );
  const {
    currentFeedbackPage,
    currentIssuePage,
    feedbackFilter,
    issueFilter,
    sortKey,
    sortDir,
    taskSearchQuery
  } = yield select(state => state.TaskReducer);

  try {
    const { status } = yield call(postData, completeTaskUrl, {
      taskId,
      complete: true
    });
    if (status === 200) {
      yield put(
        setToastOptions({
          msg: `${taskType === 'feedback' ? 'Feedback' : 'Issue'} Completed!`,
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      if (deepLink) {
        const issueIdPromise = yield put(
          TasksApi.endpoints.getIssueById.initiate(taskId)
        );
        yield call(() => issueIdPromise);
        return yield put(completeTaskSuccess());
      }
      const { data = [], status: issueStatus = undefined } = yield call(
        getData,
        `${tasksByIdUrl}?taskId[]=${taskId}`
      );

      /**
       * If successfully reloaded update all tasks on the same view
       */
      if (issueStatus === 200 && data.length > 0) {
        const updatedTask = data[0];
        if (edit) {
          yield put(setModalData(updatedTask));
          if (window.location.pathname.includes('/profile')) {
            if (updatedTask.employeeId) {
              yield put(
                getTasksByEmployeeId({
                  employeeId: updatedTask.employeeId,
                  roundingTypes: ['employee', 'cned', 'csrn']
                })
              );
            } else if (updatedTask.accountNum) {
              yield put(
                // patient tasks
                getTasksByAccountNum({
                  facilityMnemonic: authorizedUser?.facilityId,
                  accountNumber: updatedTask.accountNum
                })
              );
            }
            return yield put(completeTaskSuccess());
          }
        }
        if (taskType === 'issue') {
          const issuesAction = useIssuesApi({
            authorizedUser,
            currentIssuePage,
            issueFilter,
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortDir,
            sortKey
          });
          yield put(issuesAction);
          return yield put(completeTaskSuccess());
        }
        if (taskType === 'feedback') {
          const feedbackAction = useFeedbackApi({
            authorizedUser,
            currentFeedbackPage,
            feedbackFilter,
            searchTerm: taskSearchQuery,
            selectedDelegate: selectedDelegatedEmployee.hcaid,
            selectedUnits,
            selectedEmpDepartments,
            sortKey,
            sortDir
          });
          yield put(feedbackAction);
          return yield put(completeTaskSuccess());
        }
      }
      yield put(
        setToastOptions({
          msg: `Failed to Refresh ${
            taskType === 'feedback' ? 'Feedback' : 'Issues'
          }!`,
          feedback: 'error',
          duration: null,
          speed: 500,
          question: true
        })
      );
      yield put(slideIn());
      return yield put(completeTaskSuccess());
    }
    yield put(completeTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Complete!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(completeTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Complete!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}

export function* deleteTaskSaga({ payload }) {
  const { accountNum, employeeId, facilityId, taskId, taskType } = payload;

  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { selectedUnits, selectedEmpDepartments } = yield select(
    state => state.ConfigReducer
  );
  const { selectedDelegatedEmployee } = yield select(
    state => state.EmployeeReducer
  );
  const {
    currentFeedbackPage,
    currentIssuePage,
    feedbackFilter,
    issueFilter,
    sortKey,
    sortDir,
    taskSearchQuery
  } = yield select(state => state.TaskReducer);

  const url = deleteTaskUrl;

  try {
    const { status } = yield call(postData, url, { taskId });
    if (status === 200) {
      if (taskType === 'issue') {
        // appInsights.trackEvent({
        //   name: 'Issue_Deleted'
        // });
      } else {
        // appInsights.trackEvent({
        //   name: 'Feedback_Deleted'
        // });
      }
      yield put(
        setToastOptions({
          msg: `${taskType === 'feedback' ? 'Feedback' : 'Issue'} Deleted!`,
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      /**
       * Update all tasks on the same view
       */
      if (window.location.pathname.includes('/profile')) {
        if (employeeId) {
          yield put(
            getTasksByEmployeeId({
              employeeId,
              roundingTypes: ['employee', 'cned', 'csrn']
            })
          );
        } else if (accountNum) {
          yield put(
            // patient tasks
            getTasksByAccountNum({
              facilityMnemonic: facilityId,
              accountNumber: accountNum
            })
          );
        }
        yield delay(400);
        return yield put(deleteTaskSuccess());
      }
      if (window.location.pathname.includes('/issues')) {
        const issuesAction = useIssuesApi({
          authorizedUser,
          currentIssuePage,
          issueFilter,
          searchTerm: taskSearchQuery,
          selectedDelegate: selectedDelegatedEmployee.hcaid,
          selectedUnits,
          selectedEmpDepartments,
          sortDir,
          sortKey
        });
        yield put(issuesAction);
        yield delay(400);
        return yield put(deleteTaskSuccess());
      }
      if (window.location.pathname.includes('/feedback')) {
        const feedbackAction = useFeedbackApi({
          authorizedUser,
          currentFeedbackPage,
          feedbackFilter,
          searchTerm: taskSearchQuery,
          selectedDelegate: selectedDelegatedEmployee.hcaid,
          selectedUnits,
          selectedEmpDepartments,
          sortKey,
          sortDir
        });
        yield put(feedbackAction);
        yield delay(400);
        return yield put(deleteTaskSuccess());
      }

      yield delay(400);
      return yield put(deleteTaskSuccess());
    }
    yield put(deleteTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Delete!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(deleteTaskFailure());
    yield put(
      setToastOptions({
        msg: `${
          taskType === 'feedback' ? 'Feedback' : 'Issue'
        } Failed to Delete!`,
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}

export function* getCategoriesByTypeSaga({ payload }) {
  // Query params are categoryFor, categoryType, feedbackType
  const params = new URLSearchParams(payload).toString();

  const url = `${getCategoriesByTypeUrl}?${params}`;

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      return yield put(getCategoriesByTypeSuccess(data.categories));
    }
    return yield put(getCategoriesByTypeFailure());
  } catch (error) {
    console.log(error);
    return yield put(getCategoriesByTypeFailure());
  }
}

// Fetch service central category's ticket details by ticketNumber
export function* getDetailsByTicketNumSaga({ payload }) {
  const url = `${ticketDetailsUrl}?ticketNumber=${payload}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(getDetailsByTicketNumSuccess(data.url));
    }
    return yield put(getDetailsByTicketNumFailure());
  } catch (error) {
    console.log(error);
    return yield put(getDetailsByTicketNumFailure());
  }
}

// Fetch service central category's locations
export function* getSCLocationsSaga() {
  const url = `${getECLocationsNewUrl}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(getSCLocationsSuccess(data.result));
    }
    return yield put(getSCLocationsFailure());
  } catch (error) {
    console.log(error);
    return yield put(getSCLocationsFailure());
  }
}

// Fetch service central category's locations
export function* getGELocationsSaga() {
  const url = `${getGELocationsUrl}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(getGELocationsSuccess(data.result));
    }
    return yield put(getGELocationsFailure());
  } catch (error) {
    console.log(error);
    return yield put(getGELocationsFailure());
  }
}

// Verify control number entered
export function* verifyControlNumberSaga({ payload }) {
  const url = `${verifyControlNumberUrl}?controlNumber=${payload}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(
        verifyControlNumberSuccess(data.result[0] || data.result)
      );
    }
    yield put(verifyControlNumberFailure());
    yield put(
      setToastOptions({
        msg: 'Invalid Control Number',
        feedback: 'error',
        duration: 1500,
        speed: 500
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    return yield put(verifyControlNumberFailure());
  }
}

export function* getTaskDiscussionSaga({ payload }) {
  const id = payload;

  const url = `${discussionUrl}/${id}`;
  const { modalData } = yield select(state => state.ModalReducer);

  try {
    const { data, status } = yield call(getData, url);

    if (status === 200) {
      // get the task again to refresh the convo
      return yield put(setModalData({ ...modalData, discussion: data }));
    }
    return yield put(setModalData({ ...modalData, discussion: [] }));
  } catch (error) {
    console.log(error);
    return yield put(setModalData({ ...modalData, discussion: [] }));
  }
}

export function* postCommentToTaskSaga({ payload }) {
  const {
    task,
    comment: { id, comment }
  } = payload;

  const url = `${discussionUrl}/${id}/comment`;

  try {
    const { data, status } = yield call(postData, url, comment);

    if (status === 200) {
      yield put(postCommentToStoplightSuccess(data));
      // TODO: Is this needed
      const { authorizedUser } = yield select(state => state.AuthorizedUser);
      const { selectedUnits } = yield select(state => state.ConfigReducer);
      const { selectedDelegatedEmployee } = yield select(
        state => state.EmployeeReducer
      );
      const {
        currentIssuePage,
        issueFilter,
        sortKey,
        sortDir,
        taskSearchQuery
      } = yield select(state => state.TaskReducer);

      const issuesAction = useIssuesApi({
        authorizedUser,
        currentIssuePage,
        issueFilter,
        searchTerm: taskSearchQuery,
        selectedDelegate: selectedDelegatedEmployee.hcaid,
        selectedUnits,
        sortDir,
        sortKey
      });
      yield put(issuesAction);

      return yield put(
        setModalData({
          ...task,
          discussion: data,
          latestComment: data.comments[data.comments.length - 1]
        })
      );
    }
    yield put(
      setToastOptions({
        msg: 'Failed to Post Comment!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Failed to Post Comment!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}

export function* searchIssuesSaga({ payload }) {
  const { query, origin, page } = payload;

  const { facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );

  const url = `${issueSearchUrl}?facilityId=${facilityId}&query=${query}&origin=${origin}&page=${page}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(searchIssuesSuccess(data));
    }
    return yield put(searchIssuesFailure());
  } catch (error) {
    console.log(error);
    return yield put(searchIssuesFailure());
  }
}

export function* getIssuesByUnitIdsOrEmployeeListSaga({ payload }) {
  const { location, filterData } = payload;

  const { hcaid, facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );

  const params = new URLSearchParams(filterData).toString();

  if (
    (hcaid &&
      ((facilityId && location === 'patient' && filterData.units) ||
        (location === 'employee' && filterData?.employeeIds?.length > 0))) ||
    location === 'reports'
  ) {
    let url = `${getPatientIssuesUrl}?${params}`;
    if (location === 'employee') {
      url = `${getEmployeeIssuesUrl}?${params}`;
    }
    if (location === 'reports') {
      url = `${url}&metricsOnly=true`;
    }
    try {
      const { data, status } = yield call(getData, url);
      if (status === 200) {
        if (location === 'reports') {
          return yield put(
            getIssuesByUnitIdsOrEmployeeMetricsSuccess({
              totalCount: data.totalRecordsCount ?? 0,
              completedRecordsCount: data.completeRecordsCount ?? 0,
              openedRecordsCount: data.openRecordsCount ?? 0
            })
          );
        }
        let arrData = [];
        if (location === 'employee') {
          arrData = data.employeeIssueList;
        } else {
          arrData = data.patientIssueList;
        }

        const cleanData = arrData?.map(task => {
          let taskStatus;
          if (task.completed) {
            taskStatus = 'Completed';
          } else {
            taskStatus = 'Opened';
          }
          return {
            friendlyId: task.friendlyId,
            taskId: task.taskId,
            userId: task.userId,
            taskType: location === 'employee' ? task.tasktype : task.taskType,
            category: task.category,
            taskSubtype: task.taskSubtype,
            subCategory: task.subCategory,
            taskCategory: task.taskCategory,
            title: task.title,
            completed: task.completed,
            completedDate: task.completedDate,
            taskDate: task.taskDate,
            description: task.description,
            facilityId: task.facilityId,
            patientFirstName: task.patientFirstName,
            patientLastName: task.patientLastName,
            patientFullName: task.patientFullName,
            employeeFirstName: task.employeeFirstName,
            employeeLastName: task.employeeLastName,
            employeeFullName: task.employeeFullName,
            employeeId: task.employeeId && task.employeeId.toUpperCase(),
            room: task.room,
            bed: task.bed,
            roundingType: task.roundingType,
            sharedWith: task.sharedWith,
            spotlight: task.isCopiedToStoplight,
            taskStatus,
            updated: task.updated,
            updatedBy: task.updatedBy,
            unitId: task.unitId,
            userFullName: task.userFullName,
            stoplightId: task.stoplightId,
            ticketNumber: task.ticketNumber,
            latestComment: task.latestComment,
            discussion: task.discussion,
            discussionId: task.discussionId,
            department: task.department,
            departmentCode: task.departmentCode,
            departmentId: task.departmentId,
            callback: task.callback || task.u_best_contact_info,
            u_best_contact_info: task.u_best_contact_info,
            u_issue_location: task.u_issue_location,
            u_control_number: task.u_control_number,
            ec_loc: task.ec_loc,
            roundId: task.roundId,
            accountNum: task.accountNum
          };
        });
        return yield put(
          getIssuesByUnitIdsOrEmployeeListSuccess({
            issues: data.totalRecordsCount === 0 ? [] : cleanData,
            totalCount: data.totalRecordsCount
          })
        );
      }
      return yield put(getIssuesByUnitIdsOrEmployeeListFailure());
    } catch (error) {
      console.log(error);
      return yield put(getIssuesByUnitIdsOrEmployeeListFailure());
    }
  }
  return yield put(getIssuesByUnitIdsOrEmployeeListFailure());
}

export function* getIssuesByUnitIdsOrEmployeeListDownloadSaga({ payload }) {
  const { location, filterData } = payload;

  const { hcaid, facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );
  const { units } = yield select(state => state.ConfigReducer);
  const { employeesToRound } = yield select(state => state.EmployeeReducer);
  const facilities = yield select(
    state => state.ConfigReducer.userAccessibleFacilities
  );

  const params = new URLSearchParams(filterData).toString();

  yield put(getCSVToExport());
  if (
    hcaid &&
    ((facilityId && location === 'patient' && filterData.units) ||
      (location === 'employee' && employeesToRound.length > 0))
  ) {
    let totalData = [];
    let url = `${getPatientIssuesUrl}?${params}`;
    if (location === 'employee') {
      url = `${getEmployeeIssuesUrl}?${params}`;
    }
    try {
      const { data, status } = yield call(getData, `${url}&page=1`);
      if (status === 200) {
        if (data.totalRecordsCount > 50) {
          const totalPages = Math.ceil(data.totalRecordsCount / 50);
          const restData = yield all(
            [...Array(totalPages + 1).keys()]
              .slice(2)
              .map(page => call(getData, `${url}&page=${page}`))
          );
          if (restData && restData.length > 0) {
            totalData =
              location === 'employee'
                ? [
                    ...data.employeeIssueList,
                    ...restData.reduce(
                      (acc, response) =>
                        acc.concat(response.data.employeeIssueList),
                      []
                    )
                  ]
                : [
                    ...data.patientIssueList,
                    ...restData.reduce(
                      (acc, response) =>
                        acc.concat(response.data.patientIssueList),
                      []
                    )
                  ];
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (location === 'employee') {
            totalData = [...data.employeeIssueList];
          } else {
            totalData = [...data.patientIssueList];
          }
        }
      } else {
        return yield put(getCSVToExportFailure());
      }
    } catch (error) {
      console.log(error);
      return yield put(getCSVToExportFailure());
    }
    return yield put(
      getCSVToExportSuccess(
        totalData.map(item => ({
          taskDate: date.dayOfYrFull(item.taskDate),
          friendlyId: item.friendlyId,
          userFullName: item.userFullName,
          facility:
            facilities.find(f => f.facilityId === item.facilityId)?.value ||
            'Unknown',
          location:
            location === 'patient'
              ? units.find(unit => unit.unitId === item.unitId)?.unit
              : item.department
              ? item.department
              : 'Unknown',
          category: item.category || item.taskSubtype,
          subCategory: item.subCategory,
          title: text.escCSVChars(item.title),
          description: text.escCSVChars(item.description),
          name: `${
            location === 'patient'
              ? item.patientFirstName && item.patientLastName
                ? `${
                    item.patientLastName
                      ? item.patientLastName.slice(0, 3).toUpperCase()
                      : ''
                  }, ${
                    item.patientFirstName
                      ? item.patientFirstName.slice(0, 3).toUpperCase()
                      : ''
                  }`
                : ''
              : item.employeeFullName
          }`,
          latestComment: item.latestComment
            ? `${
                item.latestComment.date && date.dayOfYr(item.latestComment.date)
              } | ${item.latestComment.authorFullName} ${text.escCSVChars(
                item.latestComment.body
              )}`
            : '',
          attachedTo: item.stoplightId ? `Stoplight` : '',
          status: item.completed ? 'Completed' : 'Opened'
        }))
      )
    );
  }
  return yield put(getCSVToExportSuccess([]));
}

// Get issue report by facility mnemonic
export function* getIssueReportByFacilityIdSaga({ payload }) {
  const { location, filterData, roundingTypes } = payload;

  const taskTypeFormatLocations = ['employee', 'cned', 'csc'];

  const params = new URLSearchParams({
    ...filterData,
    roundingTypes
  }).toString();
  let url = `${issuesByFacilityUrl}?${params}`;
  if (location === 'reports') {
    url = `${url}&metricsOnly=true`;
  }

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      if (location === 'reports') {
        return yield put(
          getIssuesByUnitIdsOrEmployeeMetricsSuccess({
            totalCount: data.totalRecordsCount ?? 0,
            completedRecordsCount: data.completeRecordsCount ?? 0,
            openedRecordsCount: data.openRecordsCount ?? 0
          })
        );
      }
      const cleanData = data.issueList?.map(task => {
        let taskStatus;
        if (task.completed) {
          taskStatus = 'Completed';
        } else {
          taskStatus = 'Opened';
        }

        return {
          friendlyId: task.friendlyId,
          taskId: task.taskId,
          userId: task.userId,
          taskType: taskTypeFormatLocations.includes(location)
            ? task.tasktype
            : task.taskType,
          category: task.category,
          taskSubtype: task.taskSubtype,
          subCategory: task.subCategory,
          taskCategory: task.taskCategory,
          title: task.title,
          completed: task.completed,
          completedDate: task.completedDate,
          taskDate: task.taskDate,
          description: task.description,
          facilityId: task.facilityId,
          patientFirstName: task.patientFirstName,
          patientLastName: task.patientLastName,
          patientFullName: task.patientFullName,
          employeeFirstName: task.employeeFirstName,
          employeeLastName: task.employeeLastName,
          employeeFullName: task.employeeFullName,
          employeeId: task.employeeId && task.employeeId.toUpperCase(),
          room: task.room,
          bed: task.bed,
          roundingType: task.roundingType,
          sharedWith: task.sharedWith,
          spotlight: task.isCopiedToStoplight,
          taskStatus,
          updated: task.updated,
          updatedBy: task.updatedBy,
          unitId: task.unitId,
          userFullName: task.userFullName,
          stoplightId: task.stoplightId,
          ticketNumber: task.ticketNumber,
          latestComment: task.latestComment,
          discussion: task.discussion,
          discussionId: task.discussionId,
          department: task.department,
          departmentCode: task.departmentCode,
          departmentId: task.departmentId,
          callback: task.callback || task.u_best_contact_info,
          u_best_contact_info: task.u_best_contact_info,
          u_issue_location: task.u_issue_location,
          u_control_number: task.u_control_number,
          ec_loc: task.ec_loc,
          roundId: task.roundId,
          accountNum: task.accountNum
        };
      });

      return yield put(
        getIssueReportByFacilityIdSuccess({
          issues: data.totalRecordsCount === 0 ? [] : cleanData,
          totalCount: data.totalRecordsCount
        })
      );
    }
    return yield put(getIssueReportByFacilityIdFailure());
  } catch (error) {
    console.log(error);
    return yield put(getIssueReportByFacilityIdFailure());
  }
}

// Get issue report by facility mnemonic for csv export
export function* getIssueReportByFacilityIdDownloadSaga({ payload }) {
  const { location, filterData, roundingTypes } = payload;

  const { units } = yield select(state => state.ConfigReducer);
  const facilities = yield select(
    state => state.ConfigReducer.userAccessibleFacilities
  );

  const params = new URLSearchParams({
    ...filterData,
    roundingTypes
  }).toString();
  const url = `${issuesByFacilityUrl}?${params}`;

  yield put(getCSVToExport());
  let totalData = [];
  try {
    const { data, status } = yield call(getData, `${url}&page=1`);
    if (status === 200) {
      if (data.totalRecordsCount > 50) {
        const totalPages = Math.ceil(data.totalRecordsCount / 50);
        const restData = yield all(
          [...Array(totalPages + 1).keys()]
            .slice(2)
            .map(page => call(getData, `${url}&page=${page}`))
        );
        if (restData && restData.length > 0) {
          totalData = [
            ...data.issueList,
            ...restData.reduce(
              (acc, response) => acc.concat(response.data.issueList),
              []
            )
          ];
        }
      } else {
        totalData = [...data.issueList];
      }
    } else {
      return yield put(getCSVToExportFailure());
    }
  } catch (error) {
    console.log(error);
    return yield put(getCSVToExportFailure());
  }

  return yield put(
    getCSVToExportSuccess(
      totalData.map(item => ({
        taskDate: date.dayOfYrFull(item.taskDate),
        friendlyId: item.friendlyId,
        userFullName: item.userFullName,
        facility:
          facilities.find(f => f.facilityId === item.facilityId)?.value ||
          'Unknown',
        location:
          location === 'patient'
            ? units.find(unit => unit.unitId === item.unitId)?.unit
            : item.department
            ? item.department
            : 'Unknown',
        category: item.category || item.taskSubtype,
        subCategory: item.subCategory,
        title: text.escCSVChars(item.title),
        description: text.escCSVChars(item.description),
        name: `${
          location === 'patient'
            ? item.patientFirstName && item.patientLastName
              ? `${
                  item.patientLastName
                    ? item.patientLastName.slice(0, 3).toUpperCase()
                    : ''
                }, ${
                  item.patientFirstName
                    ? item.patientFirstName.slice(0, 3).toUpperCase()
                    : ''
                }`
              : ''
            : item.employeeFullName
        }`,
        latestComment: item.latestComment
          ? `${
              item.latestComment.date && date.dayOfYr(item.latestComment.date)
            } | ${item.latestComment.authorFullName} ${text.escCSVChars(
              item.latestComment.body
            )}`
          : '',
        attachedTo: item.stoplightId ? `Stoplight` : '',
        status: item.completed ? 'Completed' : 'Opened'
      }))
    )
  );
}

// FEEDBACK
export function* getFeedbackByUnitIdsSaga({ payload }) {
  const { location, filterData } = payload;
  const params = new URLSearchParams(filterData).toString();
  let url = `${getFeedbackByUnitsIdsUrl}?${params}`;
  if (location === 'reports') {
    url = `${url}&metricsOnly=true`;
  }

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      if (location === 'reports') {
        if (filterData.taskSubType === 'recognition') {
          return yield put(
            getFeedbackByUnitIdsRecognitionMetricsSuccess({
              totalCount: data.totalRecordsCount ?? 0,
              completedRecordsCount: data.completeRecordsCount ?? 0,
              openedRecordsCount: data.openRecordsCount ?? 0
            })
          );
        }
        return yield put(
          getFeedbackByUnitIdsCoachingMetricsSuccess({
            totalCount: data.totalRecordsCount ?? 0,
            completedRecordsCount: data.completeRecordsCount ?? 0,
            openedRecordsCount: data.openRecordsCount ?? 0
          })
        );
      }

      const cleanData = data.patientFeedbackList?.map(task => {
        return {
          ...task,
          roundingType: task.roundingType,
          taskType: 'feedback'
        };
      });

      return yield put(
        getFeedbackByUnitIdsSuccess({
          feedbackList: data.totalRecordsCount > 0 ? cleanData : [],
          totalCount: data.totalRecordsCount
        })
      );
    }
    return yield put(getFeedbackByUnitIdsFailure());
  } catch (error) {
    console.log(error);
    return yield put(getFeedbackByUnitIdsFailure());
  }
}

export function* getFeedbackByEmployeeListSaga({ payload }) {
  const { employeeIds } = payload;

  const params = new URLSearchParams(payload).toString();
  const url = `${getFeedbackByEmployeeListUrl}?${params}`;

  if (employeeIds?.length > 0) {
    try {
      const { data, status } = yield call(getData, url);
      if (status === 200) {
        const cleanData = data.employeeFeedbackList?.map(task => {
          return {
            ...task,
            roundingType: task.roundingType,
            taskType: 'feedback'
          };
        });

        return yield put(
          getFeedbackByEmployeeListSuccess({
            feedbackList: data.totalRecordsCount > 0 ? cleanData : [],
            totalCount: data.totalRecordsCount
          })
        );
      }
      return yield put(getFeedbackByEmployeeListFailure());
    } catch (error) {
      console.log(error);
      return yield put(getFeedbackByEmployeeListFailure());
    }
  }
  return yield put(getFeedbackByEmployeeListFailure());
}

// Get feedback list by unit ids or employee list for csv export
export function* getFeedbackByUnitIdsOrEmployeeListDownloadSaga({ payload }) {
  const { location, filterData } = payload;

  const { hcaid, facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );
  const { units } = yield select(state => state.ConfigReducer);
  const facilities = yield select(
    state => state.ConfigReducer.userAccessibleFacilities
  );

  const params = new URLSearchParams(filterData).toString();

  yield put(getCSVToExport());
  if (
    hcaid &&
    ((facilityId && location === 'patient') || location === 'employee')
  ) {
    let totalData = [];
    let url = `${getFeedbackByUnitsIdsUrl}?${params}`;
    if (location === 'employee') {
      url = `${getFeedbackByEmployeeListUrl}?${params}`;
    }
    try {
      const { data, status } = yield call(getData, `${url}&page=1`);
      if (status === 200) {
        if (data.totalRecordsCount > 50) {
          const totalPages = Math.ceil(data.totalRecordsCount / 50);
          const restData = yield all(
            [...Array(totalPages + 1).keys()]
              .slice(2)
              .map(page => call(getData, `${url}&page=${page}`))
          );
          if (restData && restData.length > 0) {
            totalData =
              location === 'employee'
                ? [
                    ...data.employeeFeedbackList,
                    ...restData.reduce(
                      (acc, response) =>
                        acc.concat(response.data.employeeFeedbackList),
                      []
                    )
                  ]
                : [
                    ...data.patientFeedbackList,
                    ...restData.reduce(
                      (acc, response) =>
                        acc.concat(response.data.patientFeedbackList),
                      []
                    )
                  ];
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (location === 'employee') {
            totalData = [...data.employeeFeedbackList];
          } else {
            totalData = [...data.patientFeedbackList];
          }
        }
      } else {
        return yield put(getCSVToExportFailure([]));
      }
    } catch (error) {
      console.log(error);
      return yield put(getCSVToExportFailure([]));
    }
    yield put(
      getCSVToExportSuccess(
        totalData.map(item => ({
          taskDate: date.dayOfYrFull(item.taskDate),
          userFullName: item.userFullName,
          facility:
            facilities.find(f => f.facilityId === item.facilityId)?.value ||
            'Unknown',
          location:
            location === 'patient'
              ? units.find(unit => unit.unitId === item.unitId)?.unit
              : item.department
              ? item.department
              : 'Unknown',
          taskSubtype: text.capFirst(item.taskSubtype),
          category: item.category || item.taskCategory.map(tc => tc),
          subCategory: item.subCategory,
          title: text.escCSVChars(item.title),
          description: text.escCSVChars(item.description),
          employeeName:
            `${item.employeeFirstName} ${item.employeeLastName}` ||
            item.employeeFullName,
          patientName: `${
            location === 'patient'
              ? item.patientFirstName && item.patientLastName
                ? `${
                    item.patientLastName
                      ? item.patientLastName.slice(0, 3).toUpperCase()
                      : ''
                  }, ${
                    item.patientFirstName
                      ? item.patientFirstName.slice(0, 3).toUpperCase()
                      : ''
                  }`
                : ''
              : ''
          }`,
          status: item.completed ? 'Completed' : 'Opened'
        }))
      )
    );
  }
  return yield put(getCSVToExportSuccess([]));
}

// Get feedback report by facility mnemonic
export function* getFeedbackReportByFacilityIdSaga({ payload }) {
  const { location, filterData, roundingTypes } = payload;

  const params = new URLSearchParams({
    ...filterData,
    roundingTypes
  }).toString();
  let url = `${feedbackByFacilityUrl}?${params}`;
  if (location === 'reports') {
    url = `${url}&metricsOnly=true`;
  }

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      if (location === 'reports') {
        // TODO: need to standardize taskSubtype casing
        if (filterData.taskSubtype === 'recognition') {
          return yield put(
            getFeedbackByUnitIdsRecognitionMetricsSuccess({
              totalCount: data.totalRecordsCount ?? 0,
              completedRecordsCount: data.completeRecordsCount ?? 0,
              openedRecordsCount: data.openRecordsCount ?? 0
            })
          );
        }
        return yield put(
          getFeedbackByUnitIdsCoachingMetricsSuccess({
            totalCount: data.totalRecordsCount ?? 0,
            completedRecordsCount: data.completeRecordsCount ?? 0,
            openedRecordsCount: data.openRecordsCount ?? 0
          })
        );
      }
      const cleanData = data.employeeFeedbackList?.map(task => {
        return {
          ...task,
          roundingType: task.roundingType,
          taskType: 'feedback'
        };
      });

      return yield put(
        getFeedbackReportByFacilityIdSuccess({
          feedbackList: data.totalRecordsCount > 0 ? cleanData : [],
          totalCount: data.totalRecordsCount
        })
      );
    }
    return yield put(getFeedbackReportByFacilityIdFailure());
  } catch (error) {
    console.log(error);
    return yield put(getFeedbackReportByFacilityIdFailure());
  }
}

// Get feedback report by facility mnemonic for csv export
export function* getFeedbackReportByFacilityIdDownloadSaga({ payload }) {
  const { location, filterData, roundingTypes } = payload;

  const { units } = yield select(state => state.ConfigReducer);
  const facilities = yield select(
    state => state.ConfigReducer.userAccessibleFacilities
  );

  const params = new URLSearchParams({
    ...filterData,
    roundingTypes
  }).toString();
  const url = `${feedbackByFacilityUrl}?${params}`;

  yield put(getCSVToExport());
  let totalData = [];
  try {
    const { data, status } = yield call(getData, `${url}&page=1`);
    if (status === 200) {
      if (data.totalRecordsCount > 50) {
        const totalPages = Math.ceil(data.totalRecordsCount / 50);
        const restData = yield all(
          [...Array(totalPages + 1).keys()]
            .slice(2)
            .map(page => call(getData, `${url}&page=${page}`))
        );
        if (restData && restData.length > 0) {
          totalData = [
            ...data.employeeFeedbackList,
            ...restData.reduce(
              (acc, response) => acc.concat(response.data.employeeFeedbackList),
              []
            )
          ];
        }
      } else {
        totalData = [...data.employeeFeedbackList];
      }
    } else {
      return yield put(getCSVToExportFailure());
    }
  } catch (error) {
    console.log(error);
    return yield put(getCSVToExportFailure());
  }
  return yield put(
    getCSVToExportSuccess(
      totalData.map(item => ({
        taskDate: date.dayOfYrFull(item.taskDate),
        userFullName: item.userFullName,
        facility:
          facilities.find(f => f.facilityId === item.facilityId)?.value ||
          'Unknown',
        location:
          location === 'patient'
            ? units.find(unit => unit.unitId === item.unitId)?.unit
            : item.department
            ? item.department
            : 'Unknown',
        taskSubtype: text.capFirst(item.taskSubtype),
        category: item.category || item.taskCategory.map(tc => tc),
        subCategory: item.subCategory,
        title: text.escCSVChars(item.title),
        description: text.escCSVChars(item.description),
        employeeName: item.employeeFullName,
        patientName: `${
          location === 'patient'
            ? item.patientFirstName && item.patientLastName
              ? `${
                  item.patientLastName
                    ? item.patientLastName.slice(0, 3).toUpperCase()
                    : ''
                }, ${
                  item.patientFirstName
                    ? item.patientFirstName.slice(0, 3).toUpperCase()
                    : ''
                }`
              : ''
            : item.employeeFullName
        }`,
        status: item.completed ? 'Completed' : 'Opened'
      }))
    )
  );
}

export function* shareAllTasksSaga({ payload }) {
  const url = shareTaskUrl;

  try {
    const { status } = yield call(postData, url, payload);
    if (status === 200) {
      return yield put(shareAllTasksSuccess());
    }
    yield put(shareAllTasksFailure());
    yield put(
      setToastOptions({
        msg: 'Failed to Share!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(shareAllTasksFailure());
    yield put(
      setToastOptions({
        msg: 'Failed to Share!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}

export function* getTasksByAccountNumSaga({ payload }) {
  const { facilityMnemonic, accountNumber } = payload;

  const url = `${getTasksByAccountNumberUrl}?accountNumber=${accountNumber}&facilityMnemonic=${facilityMnemonic}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      return yield put(getTasksByAccountNumSuccess(data));
    }
    return yield put(getTasksByAccountNumFailure());
  } catch (error) {
    return yield put(getTasksByAccountNumFailure());
  }
}

export function* getTasksByEmployeeIdSaga({ payload }) {
  const params = new URLSearchParams(payload).toString();

  const url = `${getTasksByEmployeeIdUrl}?${params}`;

  try {
    const { status, data } = yield call(getData, url);

    if (status === 200) {
      const cleanData = data.map(task => ({
        friendlyId: task.friendlyId,
        taskId: task.taskId,
        userId: task.userId,
        taskType: task.taskType,
        category: task.category,
        taskSubtype: task.taskSubtype,
        subCategory: task.subCategory,
        taskCategory: task.taskCategory,
        title: task.title,
        completed: task.completed,
        completedDate: task.completedDate,
        taskDate: task.taskDate,
        description: task.description,
        facilityId: task.facilityId,
        employeeFirstName: task.employeeFirstName,
        employeeLastName: task.employeeLastName,
        employeeFullName: task.employeeFullName,
        employeeId: task.employeeId && task.employeeId.toUpperCase(),
        roundingType: task.roundingType,
        sharedWith: task.sharedWith,
        spotlight: task.isCopiedToStoplight,
        updated: task.updated,
        updatedBy: task.updatedBy,
        userFullName: task.userFullName,
        stoplightId: task.stoplightId,
        ticketNumber: task.ticketNumber,
        latestComment: task.latestComment,
        discussion: task.discussion,
        discussionId: task.discussionId,
        department: task.department,
        departmentCode: task.departmentCode,
        departmentId: task.departmentId,
        callback: task.callback || task.u_best_contact_info,
        u_best_contact_info: task.u_best_contact_info,
        u_issue_location: task.u_issue_location,
        u_control_number: task.u_control_number,
        ec_loc: task.ec_loc,
        roundId: task.roundId
      }));
      return yield put(
        getTasksByEmployeeIdSuccess({
          issues: cleanData.filter(
            task => task.taskType === 'issue' || task.tasktype === 'issue'
          ),
          feedback: cleanData.filter(
            task => task.taskType === 'feedback' || task.tasktype === 'feedback'
          )
        })
      );
    }
    return yield put(getTasksByEmployeeIdFailure());
  } catch (error) {
    return yield put(getTasksByEmployeeIdFailure());
  }
}
